import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertDetailModal from './AlertDetailModal';
import { getDevices, getPositions, getDrivers, getRecentAlarmEvents, getRecentEvents, getGeofences, reverseGeocode, engineOn, computeRoute } from './traccar';
import { getCurfew, curfewWindow, bearing, milesBetween } from './curfewStore';
import { getAlerts, getDriverAlerts } from './vehicleAlerts';
import { accumulateIdle } from './idleStore';
import { logAlerts } from './alertLog';
import { evaluateCustomRules } from './customAlerts';
import { checkDeviceLinks, checkPlaceArrivals, deviceText, isHighwayRoad, buildPlaceTests, placeContaining } from './deviceAlerts';
import { getAlertPrefs } from './alertPrefs';
import { ALERT_SOUND_CATEGORIES, getSoundPrefs, saveSoundPrefs, soundForAlert } from './alertSound';
import { getSharedAudioCtx } from './clickSound';
import { isAcked, mergeServerAcks } from './healthAck';
import { dtcCodesFromAttrs } from './vehicleAlerts';
import { noteEvent, expireWatches } from './carWatches';
import { pendingLinks } from './brokerLink';
import { getActiveRental, rentalMath, isRentalVehicle, getRentalSettings, pendingMonthlyReport, markMonthlyReported, pendingDailyReport, odoMilesFromAttrs } from './rentalStore';

// driving-behavior alarms coming from the tracker itself
const DRIVING_ALARMS = {
  accident: { severity: 'bad', icon: '💥', title: 'CRASH DETECTED — check on the driver immediately' },
  towing: { severity: 'bad', icon: '🪝', title: 'TOW DETECTED — vehicle is moving with the ignition off' },
  hardBraking: { severity: 'bad', icon: '🛑', title: 'Sudden braking detected' },
  hardAcceleration: { severity: 'warn', icon: '💨', title: 'Harsh acceleration detected' },
  hardCornering: { severity: 'warn', icon: '↪️', title: 'Harsh cornering detected' },
  jamming: { severity: 'bad', icon: '📡', title: 'SIGNAL JAMMING DETECTED — possible theft in progress' },
  powerCut: { severity: 'bad', icon: '🔌', title: 'TRACKER POWER CUT — device unplugged or lost power' },
  tampering: { severity: 'bad', icon: '🔧', title: 'TAMPERING DETECTED — device is being interfered with' },
};

const SEEN_KEY = 'dd-announced-alerts';

const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY)) || []); } catch { return new Set(); }
};
const saveSeen = (set) => localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-500)));

// ---- Repeat suppression (cooldown) ---------------------------------------
// Many alert keys carry a Date.now() suffix, so exact-key dedupe never catches
// a repeat. We also cool down by the alert's STABLE base (key minus the trailing
// timestamp) so the same condition can't re-alert within a window — even if the
// component remounts and its in-memory recovery flags reset.
const COOL_KEY = 'dd-alert-cooldowns';
const loadCooldowns = () => { try { return JSON.parse(localStorage.getItem(COOL_KEY)) || {}; } catch { return {}; } };
const saveCooldowns = (m) => localStorage.setItem(COOL_KEY, JSON.stringify(m));
// how long to wait before the same alert may fire again (ms)
const COOLDOWN_MS = { bad: 30 * 60 * 1000, warn: 6 * 60 * 60 * 1000, info: 60 * 60 * 1000 };
// stable id for an alert = its key without a trailing 10+ digit timestamp
const baseId = (key) => String(key).replace(/:\d{10,}$/, '');

// attention chime: two quick rising notes
function chime() {
  try {
    const ctx = getSharedAudioCtx();
    if (!ctx) return;
    [[880, 0], [1320, 0.12]].forEach(([freq, delay]) => {
      const t = ctx.currentTime + delay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.3);
    });
  } catch { /* silent */ }
}

// brief spoken announcement. speechSynthesis is finicky in Chrome (voices load
// late, and it can pause itself), so we pick an English voice when available and
// resume before speaking.
function speak(text) {
  try {
    const s = window.speechSynthesis;
    if (!s) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.volume = 1;
    const voices = s.getVoices ? s.getVoices() : [];
    const en = voices.find((v) => /^en[-_]/i.test(v.lang));
    if (en) u.voice = en;
    s.resume();
    s.speak(u);
  } catch { /* silent */ }
}
// Flush the spoken-alert backlog: cancel() drops every queued utterance AND
// stops the one mid-sentence. Used by "Clear all" so a pile of alerts loaded at
// once doesn't read out a minute of backlog — only alerts arriving AFTER the
// clear will speak.
function stopSpeech() {
  try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch { /* ignore */ }
}
// warm up the voice list once (Chrome loads it asynchronously)
try { if (window.speechSynthesis && window.speechSynthesis.getVoices) window.speechSynthesis.getVoices(); } catch { /* ignore */ }

const stripEmoji = (s) => s.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '').trim();

// OS notification for an alert. Works in the browser and, once the app is added
// to the home screen (PWA), shows even when the tab is backgrounded. This is the
// same mechanism native push builds on. Silently no-ops if unsupported/denied.
function notify(al) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const body = `${al.entity ? al.entity + ' — ' : ''}${stripEmoji(al.title)}`;
    const n = new Notification(`${al.icon || '🔔'} TagAlong alert`, {
      body,
      tag: String(al.key || body), // collapse duplicates of the same alert
      icon: '/logo192.png',
      badge: '/logo192.png',
    });
    n.onclick = () => { try { window.focus(); } catch { /* ignore */ } n.close(); };
  } catch { /* silent */ }
}
// Ask for notification permission once (best-effort; some browsers need a gesture).
function askNotifyPermission() {
  try { if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
}

const onDispatchBoard = () => { try { return /^\/truckmate/i.test(window.location.pathname); } catch { return false; } };

export default function AlertWatcher({ deviceIds } = {}) {
  const navigate = useNavigate();
  // optional scope: when provided, only watch/alert for these device ids (e.g.
  // a broker only gets alerts for cars they're actively renting). Kept in a ref
  // so the polling loop always reads the latest set.
  const filterRef = useRef(null);
  filterRef.current = Array.isArray(deviceIds) ? deviceIds.map(String) : null;
  // clicking an alert jumps to Live Tracking, focused on that vehicle (which
  // shows its live location + a panel of its active alarms). Device id is the
  // 2nd segment of the alert key (e.g. "veh:12:🚨").
  const openOnMap = (al) => {
    const id = String(al.key || '').split(':')[1];
    if (!id) return;
    const tag = /[?&]tagalong\b/i.test(window.location.search) ? 'tagalong&' : '';
    navigate(`/map?${tag}device=${id}`);
    setBanners((b) => b.filter((x) => x.id !== al.id));
  };
  const [banners, setBanners] = useState([]);
  // the dispatch board stays open for days: when a new version is out, say so (a refresh loads it)
  const [newVersion, setNewVersion] = useState(false);
  useEffect(() => {
    if (!onDispatchBoard()) return undefined;
    const mine = (Array.from(document.scripts).map((x) => x.src).find((x) => /\/static\/js\/main\.[^/]+\.js$/.test(x)) || '').replace(/^https?:\/\/[^/]+/, '');
    if (!mine) return undefined;
    const check = () => fetch('/asset-manifest.json', { cache: 'no-store' }).then((r) => r.json()).then((m) => { const live = m && m.files && m.files['main.js']; if (live && live !== mine) setNewVersion(true); }).catch(() => {});
    const t = setInterval(check, 5 * 60000);
    return () => clearInterval(t);
  }, []);
  const [detail, setDetail] = useState(null); // alert whose location detail is open
  const seenRef = useRef(loadSeen());
  const cooldownRef = useRef(loadCooldowns());
  const ignRef = useRef({}); // deviceId -> last ignition state
  const firstRunRef = useRef(true); // don't replay the last window of events on open
  // mute = silence the chime + spoken voice; banners still pop up visually
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem('ta-alerts-muted') === '1'; } catch { return false; } });
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const toggleMute = () => setMuted((m) => { const nm = !m; try { localStorage.setItem('ta-alerts-muted', nm ? '1' : '0'); } catch { /* ignore */ } return nm; });
  // per-alert-type sound settings (voice vs chime), opened from the gear button
  const [soundOpen, setSoundOpen] = useState(false);
  const [soundPrefs, setSoundPrefs] = useState(() => getSoundPrefs());
  const setCatSound = (k, v) => setSoundPrefs((p) => { const np = { ...p, [k]: v }; saveSoundPrefs(np); return np; });
  const movingRef = useRef({}); // deviceId -> was moving last tick
  const fuelRef = useRef({}); // deviceId -> { lowFuel, lowRange }
  const dtcRef = useRef({}); // deviceId -> last fault-code count
  const stopRef = useRef({}); // deviceId -> highway-stop tracking
  const towRef = useRef({}); // deviceId -> tow/theft tracking
  const battRef = useRef({}); // deviceId -> low-battery flag
  const speedRef = useRef({}); // deviceId -> { step, over } for 5-mph speeding bands

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const [rawDevices, positions, drivers, fencesNow] = await Promise.all([
          getDevices(), getPositions(), getDrivers().catch(() => []), getGeofences().catch(() => []),
        ]);
        if (cancelled) return;
        mergeServerAcks(rawDevices); // sync "marked resolved" so acked codes don't re-alarm
        const placeTests = buildPlaceTests(fencesNow); // ⭐ saved-place contains-tests for "arrived safe"
        // alerts speak to the customer, so use the name they gave the car
        // (attributes.displayName); fall back to the account-number name.
        let devices = rawDevices.map((d) => ((d.attributes || {}).displayName ? { ...d, name: d.attributes.displayName } : d));
        if (filterRef.current) devices = devices.filter((d) => filterRef.current.includes(String(d.id)));

        const found = [];
        const stopCandidates = [];
        const curfewCandidates = [];
        devices.forEach((d) => {
          const p = positions.find((x) => x.deviceId === d.id);
          getAlerts(d, p).forEach((al) => {
            found.push({ key: `veh:${d.id}:${al.icon}`, entity: d.name, ...al });
          });

          // ---- rental alerts (only for vehicles put in rental mode) ----
          if (isRentalVehicle(d.id)) {
            const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
            const rental = getActiveRental(d.id);
            if (rental) {
              const odoMi = p && p.attributes ? odoMilesFromAttrs(p.attributes) : null;
              const m = rentalMath(rental, odoMi);
              if (m.overdue) {
                found.push({ key: `rentover:${d.id}:${rental.id}`, entity: d.name, severity: 'warn', icon: '⏰', title: `Rental overdue — ${rental.renter}`, info: `${d.name} was due back ${new Date(m.dueAt).toLocaleString()}. Charged so far ${money(m.total)}. Extend the rental or arrange return.` });
              }
              if (m.overMiles) {
                found.push({ key: `rentmiles:${d.id}:${rental.id}`, entity: d.name, severity: 'warn', icon: '🛣️', title: `Rental over mileage — ${Math.round(m.overageMiles)} mi over`, info: `${rental.renter} passed the ${m.allowedMiles}-mile allowance. Overage ${money(m.overageCharge)} at $${rental.overageRate}/mi will be added to the total.` });
              }
            }
            // optional end-of-day miles report (per-vehicle toggle)
            if (getRentalSettings(d.id).dailyReport) {
              const odoMi = p && p.attributes ? odoMilesFromAttrs(p.attributes) : null;
              const daily = pendingDailyReport(d.id, odoMi);
              if (daily) {
                found.push({ key: `rentday:${d.id}:${daily.day}`, entity: d.name, severity: 'info', icon: '📅', title: `Daily miles — ${Math.round(daily.miles)} mi`, info: `${d.name} drove ${Math.round(daily.miles)} miles on ${new Date(daily.day).toLocaleDateString()}.` });
              }
            }
            // end-of-month revenue summary (once per car per month)
            const monthly = pendingMonthlyReport(d.id);
            if (monthly) {
              const money2 = (n) => `$${(Number(n) || 0).toFixed(2)}`;
              found.push({ key: `rentmonth:${d.id}:${monthly.key}`, entity: d.name, severity: 'info', icon: '📅', title: `${monthly.month.toLocaleDateString('en-US', { month: 'long' })} rental revenue — ${money2(monthly.sum.revenue)}`, info: `${d.name}: ${monthly.sum.count} rental(s), ${Math.round(monthly.sum.miles)} miles, ${monthly.sum.days} day(s) rented last month.` });
              markMonthlyReported(monthly.key);
            }
          }
          // per-vehicle speed thresholds
          if (p) {
            const mph = Math.round((p.speed || 0) * 1.15078);
            const warn = Number((d.attributes || {}).speedWarnMph) || 0;
            const max = Number((d.attributes || {}).speedMaxMph) || 0;
            const posKey = p.id || p.fixTime;

            // GPS device backup-battery — warns the tracker lost vehicle power and
            // is draining (about to disconnect). 50% heads-up, 10% critical.
            const pa = p.attributes || {};
            // Only alarm if the tracker actually HAS a backup battery (voltage in
            // Li range). OBD units with no battery report 0 V — skip them so they
            // don't false-fire "critical 0%".
            const hasBatt = pa.battery != null && pa.battery >= 3.0;
            const devPct = hasBatt
              ? (pa.batteryLevel != null ? pa.batteryLevel : Math.round(Math.min(100, Math.max(0, (pa.battery - 3.5) / (4.2 - 3.5) * 100))))
              : (pa.battery == null && pa.batteryLevel != null && pa.batteryLevel > 0 ? pa.batteryLevel : null);
            if (devPct != null) {
              if (devPct <= 10) {
                found.push({ key: `devbatt10:${d.id}:${Date.now()}`, entity: d.name, severity: 'bad', icon: '🪫', title: `Tracker battery critical — ${devPct}%`, info: `${d.name}'s GPS device is on backup power at ${devPct}% and about to shut off — you may lose tracking. It looks unplugged; check the connection.` });
              } else if (devPct <= 50) {
                found.push({ key: `devbatt50:${d.id}:${Date.now()}`, entity: d.name, severity: 'warn', icon: '🔋', title: `Tracker battery low — ${devPct}%`, info: `${d.name}'s GPS device is running on its backup battery at ${devPct}% — it may have lost vehicle power.` });
              }
            }
            const spdPlace = p ? { lat: p.latitude, lng: p.longitude, speed: mph, at: p.fixTime || p.deviceTime, device: d.name, imei: d.uniqueId, address: p.address || '' } : undefined;
            // Speeding alerts fire at the set threshold, then only once per +5 mph
            // band above it (not every single mph). A distinct alert also fires the
            // first time it crosses the over-speed limit. Resets below the threshold.
            const spThresh = warn || max;
            const sref = speedRef.current[d.id] || { step: -1, over: false };
            if (!spThresh || mph < spThresh) {
              speedRef.current[d.id] = { step: -1, over: false };
            } else {
              const step = Math.floor((mph - spThresh) / 5); // 0 = threshold..+4, 1 = +5..+9 …
              const isOver = !!(max && mph >= max);
              if (step > sref.step || (isOver && !sref.over)) {
                if (isOver) {
                  found.push({ key: `ovspd:${d.id}:${step}:${posKey}`, entity: d.name, severity: 'bad', icon: '🚨', title: `Over speed — ${mph} mph`, info: `${d.name} is over its ${max} mph limit (now ${mph} mph).`, place: spdPlace && { ...spdPlace, title: 'Over speed' } });
                } else {
                  found.push({ key: `spd:${d.id}:${step}:${posKey}`, entity: d.name, severity: 'warn', icon: '⏩', title: `Speeding — ${mph} mph`, info: `${d.name} is over the ${warn} mph warning (now ${mph} mph).`, place: spdPlace && { ...spdPlace, title: 'Speeding' } });
                }
              }
              speedRef.current[d.id] = { step: Math.max(step, sref.step), over: sref.over || isOver };
            }
            // ignition — use a robust "engine on" (flag OR moving OR RPM) so the
            // flaky OBD ignition bit can't fire a false "turned on/off". Also
            // require the new state to hold for 2 readings before alerting.
            const rpmNow = Number((p.attributes || {}).io36) || 0;
            // Freshness gate — same 7-min window the map uses. A stale record is
            // treated as OFF so the watcher and the map agree, AND so a later fresh
            // record reads as a real off→on. We do NOT announce a turn-OFF caused
            // only by staleness (can't confirm the key turned); a real fresh "off"
            // record still alerts.
            const pT = [p.serverTime, p.deviceTime, p.fixTime].map((t) => (t ? new Date(t).getTime() : 0));
            const pLastSeen = Math.max(0, ...pT);
            const pStale = pLastSeen ? (Date.now() - pLastSeen > 7 * 60 * 1000) : true;
            const engOn = pStale ? false : engineOn(p.attributes, p.speed);
            const st = ignRef.current[d.id] || { on: engOn, pending: engOn, count: 0 };
            if (engOn === st.on) { st.pending = engOn; st.count = 0; } // steady
            else if (engOn === st.pending) { st.count += 1; } // same new reading again
            else { st.pending = engOn; st.count = 1; } // new candidate
            if (st.count >= 2 && st.on !== st.pending) { // confirmed change
              st.on = st.pending;
              if (st.on) {
                found.push({ key: `ignon:${d.id}:${p.fixTime || posKey}`, entity: d.name, severity: 'warn', icon: '🔑', title: 'Vehicle turned on', info: `${d.name}'s ignition was switched on at ${new Date(p.fixTime || Date.now()).toLocaleTimeString()}.` });
                noteEvent(d.id, 'ignitionOn', d.name).forEach((al) => found.push(al));
              } else if (!pStale) {
                const offTime = new Date(p.fixTime || Date.now()).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                // "Arrived safe" — key turned OFF while parked inside a saved ⭐ place.
                // One calm, positive ping ("Emma arrived at School, 7:42 AM") instead of
                // the bare "turned off". Gated by the arrivals preference.
                const arrivedPlace = getAlertPrefs(d.id).builtins.arrival && getAlertPrefs(d.id).builtins.arrival.on
                  ? placeContaining(p.latitude, p.longitude, placeTests) : null;
                if (arrivedPlace) {
                  found.push({
                    key: `arrsafe:${d.id}:${p.fixTime || posKey}`, entity: d.name, severity: 'good', icon: '✅',
                    title: `${d.name} arrived at ${arrivedPlace}`,
                    info: `${d.name} arrived safe at ${arrivedPlace}, ${offTime}. Engine off, parked.`,
                    text: `✅ ${d.name} arrived at ${arrivedPlace}, ${offTime}.`,
                    place: { lat: p.latitude, lng: p.longitude, speed: 0, at: p.fixTime, device: d.name, imei: d.uniqueId || '', address: p.address || arrivedPlace, title: `Arrived at ${arrivedPlace}` },
                  });
                } else {
                  found.push({ key: `ignoff:${d.id}:${p.fixTime || posKey}`, entity: d.name, severity: 'info', icon: '🅿️', title: 'Vehicle turned off', info: `${d.name}'s ignition was switched off at ${offTime}.` });
                }
                noteEvent(d.id, 'ignitionOff', d.name).forEach((al) => found.push(al));
              }
            }
            ignRef.current[d.id] = st;
            const ign = st.on;

            // started moving (for "alert me when it starts moving" watches)
            const prevMoving = movingRef.current[d.id];
            const movingNow = mph > 5;
            if (prevMoving === false && movingNow) noteEvent(d.id, 'moving', d.name).forEach((al) => found.push(al));
            movingRef.current[d.id] = movingNow;

            // ---- signal jamming: GPS/GSM being blocked (a classic car-theft tactic) ----
            const jamAttrs = p.attributes || {};
            if (jamAttrs.jamming === true || Number(jamAttrs.io249) === 1 || jamAttrs.blocked === true) {
              found.push({ key: `jam:${d.id}:${p.fixTime || posKey}`, entity: d.name, severity: 'bad', icon: '📡', title: 'SIGNAL JAMMING DETECTED', info: `${d.name}'s tracker is detecting GPS/GSM jamming — someone may be blocking the signal to hide the vehicle. Check on it right away.`, text: `🚨 ${d.name}: signal jamming detected — possible theft. Check Live Tracking now.` });
            }

            // broker approval requests waiting on the owner
            pendingLinks(d).forEach((lnk) => {
              found.push({ key: `brokerreq:${d.id}:${lnk.brokerId}`, entity: d.name, severity: 'warn', icon: '🤝', title: 'Broker approval pending', info: `${lnk.brokerName}${lnk.brokerCompany ? ` (${lnk.brokerCompany})` : ''} wants to add ${d.name} to their rental fleet. Approve or decline it on the vehicle profile.` });
            });

            // ---- fuel + diagnostics (from the OBD reader) ----
            const attrs = p.attributes || {};
            const fuel = attrs.io48 != null ? Number(attrs.io48) : null; // %
            const tankRange = Number((d.attributes || {}).tankRangeMiles) || 300; // full-tank miles
            const lowRangeMi = Number((d.attributes || {}).lowRangeMiles) || 50;
            const lowFuelPct = Number((d.attributes || {}).lowFuelPct) || 15;
            const fs = fuelRef.current[d.id] || { lowFuel: false, lowRange: false };
            // only warn about fuel when the car is actually in use (ignition on
            // or moving) — no point nagging about a parked, powered-off car.
            const inUse = ign === true || mph > 1;
            if (fuel != null && inUse) {
              const range = Math.round((fuel / 100) * tankRange);
              if (!fs.lowRange && range < lowRangeMi) {
                found.push({ key: `lowrange:${d.id}:${Date.now()}`, entity: d.name, severity: 'warn', icon: '⛽', title: `Low fuel — ~${range} mi of range`, info: `${d.name} has about ${range} miles of range left (under ${lowRangeMi}).`, text: deviceText.lowRange(d.name, range, lowRangeMi) });
                fs.lowRange = true;
              } else if (fs.lowRange && range > lowRangeMi + 15) fs.lowRange = false;
              if (!fs.lowFuel && fuel <= lowFuelPct) {
                found.push({ key: `lowfuel:${d.id}:${Date.now()}`, entity: d.name, severity: 'warn', icon: '⛽', title: `Low fuel — ${fuel}%`, info: `${d.name}'s fuel level is low (${fuel}%).`, text: deviceText.lowFuel(d.name, fuel) });
                fs.lowFuel = true;
              } else if (fs.lowFuel && fuel > lowFuelPct + 5) fs.lowFuel = false;
            }
            fuelRef.current[d.id] = fs;

            // low battery (vehicle voltage below the set threshold)
            const volt = attrs.power != null ? Number(attrs.power) : null;
            const lowV = Number((d.attributes || {}).lowVoltage) || 12.2;
            const br = battRef.current[d.id] || { low: false };
            if (volt != null && volt > 5) {
              if (!br.low && volt < lowV) {
                found.push({ key: `lowbatt:${d.id}:${Date.now()}`, entity: d.name, severity: 'warn', icon: '🔋', title: `Low battery — ${volt}V`, info: `${d.name}'s battery is low (${volt}V, under ${lowV}V).`, text: `🔋 ${d.name}'s battery is low at ${volt}V — it may struggle to start. Have it checked.` });
                br.low = true;
              } else if (br.low && volt > lowV + 0.3) br.low = false;
            }
            battRef.current[d.id] = br;

            // registration / license-plate expiry — warn a month out, then weekly
            {
              const plateExp = (d.attributes || {}).vehPlateExp;
              if (plateExp) {
                const days = Math.ceil((new Date(plateExp) - Date.now()) / 86400000);
                if (days <= 30) {
                  const pl = (d.attributes || {}).vehPlate;
                  const plate = pl ? ` (${pl})` : '';
                  const wk = Math.max(0, Math.floor((30 - days) / 7)); // bucket so it re-alerts ~weekly
                  found.push({
                    key: `reg:${d.id}:${plateExp}:${wk}`, entity: d.name, severity: days < 0 ? 'bad' : 'warn', icon: '🪪',
                    title: days < 0 ? `Registration expired${plate}` : `Registration expires in ${days} day${days === 1 ? '' : 's'}${plate}`,
                    info: `${d.name}'s license-plate registration${plate} ${days < 0 ? 'has expired' : `expires ${new Date(plateExp).toLocaleDateString()}`}. Renew it to avoid a ticket.`,
                    text: `🪪 ${d.name}: license-plate registration${plate} ${days < 0 ? 'has EXPIRED' : `expires in ${days} day(s) (${new Date(plateExp).toLocaleDateString()})`}. Time to renew.`,
                  });
                }
              }
            }

            // check engine — show the actual trouble code(s) when the tracker
            // sends them (io281 / dtcs); otherwise fall back to the count (io30).
            const dtc = attrs.io30 != null ? Number(attrs.io30) : 0;
            const dtcCodes = dtcCodesFromAttrs(attrs);
            const prevDtc = dtcRef.current[d.id];
            // don't alarm a code the owner already marked resolved (synced via healthAcks)
            if (dtc > 0 && (prevDtc == null || dtc > prevDtc) && !isAcked(d.id, 'check-engine', String(dtc))) {
              const codeLabel = dtcCodes.length ? dtcCodes.map((c) => c.code).join(', ') : `${dtc} code${dtc === 1 ? '' : 's'}`;
              const codeInfo = dtcCodes.length
                ? `${d.name} check-engine: ${dtcCodes.map((c) => `${c.code} — ${c.meaning}`).join('; ')}.`
                : `${d.name} has ${dtc} active trouble code${dtc === 1 ? '' : 's'} — the check-engine light is likely on. Have it scanned to read the exact code, or plug in an OBD reader.`;
              found.push({ key: `dtc:${d.id}:${dtc}:${Date.now()}`, entity: d.name, severity: 'bad', icon: '🛠️', title: `Check engine — ${codeLabel}`, info: codeInfo, text: deviceText.checkEngine(d.name, dtc, dtcCodes) });
            }
            dtcRef.current[d.id] = dtc;

            // stopped on a highway too long (possible breakdown / accident)
            const nowTs = Date.now();
            const stopMin = Number((d.attributes || {}).highwayStopMin) || 17;
            const sr = stopRef.current[d.id] || { stoppedSince: null, lastHighwayTs: 0, alerted: false, checked: false };
            if (mph >= 45) sr.lastHighwayTs = nowTs; // was travelling at highway speed
            if (mph <= 3) {
              if (!sr.stoppedSince) { sr.stoppedSince = nowTs; sr.alerted = false; sr.checked = false; }
              const stoppedMs = nowTs - sr.stoppedSince;
              const recentHighway = (nowTs - sr.lastHighwayTs) < 8 * 60000; // was on the highway shortly before stopping
              if (!sr.alerted && !sr.checked && recentHighway && stoppedMs > stopMin * 60000 && p.latitude) {
                sr.checked = true; // reverse-geocode just once per stop
                stopCandidates.push({ d, lat: p.latitude, lng: p.longitude, mins: Math.round(stoppedMs / 60000) });
              }
            } else { sr.stoppedSince = null; sr.alerted = false; sr.checked = false; }
            stopRef.current[d.id] = sr;

            // tow-away / theft — the car is moving with the ignition off (being towed or stolen)
            const igOn = (p.attributes || {}).ignition === true;
            const tr = towRef.current[d.id] || { streak: 0, alerted: false };
            if (mph > 7 && !igOn) {
              tr.streak = (tr.streak || 0) + 1;
              if (tr.streak >= 2 && !tr.alerted && p.latitude) {
                const mapUrl = `https://www.google.com/maps?q=${p.latitude},${p.longitude}`;
                const loc = p.address || `${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}`;
                found.push({ key: `tow:${d.id}:${p.fixTime || Date.now()}`, entity: d.name, severity: 'bad', icon: '🚨', title: 'Possible tow-away / theft', info: `${d.name} is moving ${mph} mph with the engine off.`, text: deviceText.towTheft(d.name, loc, mapUrl) });
                tr.alerted = true;
              }
            } else { tr.streak = 0; if (igOn) tr.alerted = false; }
            towRef.current[d.id] = tr;

            // aggressive RPM
            const rpmLimit = Number((d.attributes || {}).rpmAlertRpm) || 0;
            const rpm = Number((p.attributes || {}).io36) || 0;
            if (rpmLimit && rpm >= rpmLimit) {
              found.push({ key: `rpm:${d.id}:${posKey}`, entity: d.name, severity: 'warn', icon: '🔧', title: `Aggressive RPM — ${rpm} rpm`, info: `${d.name}'s engine revved to ${rpm} rpm (limit ${rpmLimit}) — possible hard/aggressive acceleration.` });
            }

            // smart curfew — after curfew, is the car heading home or moving away?
            const cf = getCurfew(d.id);
            if (cf.enabled && cf.homeLat != null && p.latitude != null) {
              const win = curfewWindow(cf);
              if (win.active) {
                const distHome = milesBetween(p.latitude, p.longitude, cf.homeLat, cf.homeLng);
                const engOnNow = engineOn(p.attributes, p.speed);
                const movingNow = mph > 3 || engOnNow;
                if (distHome <= 0.12) {
                  curfewCandidates.push({ d, p, cf, nightKey: win.nightKey, state: 'home', distHome });
                } else if (movingNow) {
                  const brgHome = bearing(p.latitude, p.longitude, cf.homeLat, cf.homeLng);
                  const course = Number(p.course) || 0;
                  const diff = Math.min(Math.abs(course - brgHome), 360 - Math.abs(course - brgHome));
                  curfewCandidates.push({ d, p, cf, nightKey: win.nightKey, state: diff <= 55 ? 'heading' : 'away', distHome });
                }
              }
            }
          }
        });

        // resolve curfew candidates (needs an async Routes call for the ETA)
        for (const c of curfewCandidates) {
          const { d, p, cf, nightKey, state, distHome } = c;
          const carMph = Math.round((p.speed || 0) * 1.15078);
          const place = { lat: p.latitude, lng: p.longitude, speed: carMph, at: p.fixTime, device: d.name, imei: d.uniqueId || '', address: p.address || '' };
          if (state === 'home') {
            found.push({ key: `curfewsafe:${d.id}:${nightKey}`, entity: d.name, severity: 'good', icon: '🏡', title: `${d.name} made it home`, info: `${d.name} is back at ${cf.homeName || 'home'} after curfew — all good.`, place: { ...place, title: 'Home' } });
          } else if (state === 'heading') {
            const route = await computeRoute({ lat: p.latitude, lng: p.longitude }, { lat: cf.homeLat, lng: cf.homeLng }).catch(() => null);
            const min = route && route.seconds ? Math.max(1, Math.round(route.seconds / 60)) : null;
            const etaTxt = min != null ? new Date(Date.now() + min * 60000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
            found.push({ key: `curfewhome:${d.id}:${nightKey}`, entity: d.name, severity: 'info', icon: '🏠',
              title: `${d.name} heading home${etaTxt ? ` — ETA ${etaTxt}` : ''}`,
              info: `It's past curfew (${cf.time}), but ${d.name} is driving toward ${cf.homeName || 'home'} — ${distHome.toFixed(1)} mi out${min != null ? `, about ${min} min away` : ''}.`,
              place: { ...place, title: 'Heading home' } });
          } else {
            found.push({ key: `curfewout:${d.id}:${nightKey}`, entity: d.name, severity: 'warn', icon: '🌙',
              title: `${d.name} out past curfew`,
              info: `${d.name} is out after curfew (${cf.time}) and not heading home — ${distHome.toFixed(1)} mi from ${cf.homeName || 'home'}, near ${p.address || `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`}.`,
              place: { ...place, title: 'Out past curfew' } });
          }
        }
        // accumulate idle time and raise hourly idle warnings
        accumulateIdle(devices, positions).forEach((al) => found.push(al));

        // tracker offline / reconnected / moved to a new vehicle
        (await checkDeviceLinks(devices, positions)).forEach((al) => found.push(al));

        // finish any time-boxed watches whose window ended (report the tally)
        const nameById = {}; devices.forEach((d) => { nameById[d.id] = d.name; });
        expireWatches(nameById).forEach((al) => found.push(al));

        // confirm highway for any long-stop candidates, then alert
        for (const c of stopCandidates) {
          const rev = await reverseGeocode(c.lat, c.lng);
          if (isHighwayRoad(rev)) {
            const sr = stopRef.current[c.d.id]; if (sr) sr.alerted = true;
            found.push({ key: `hwystop:${c.d.id}:${sr ? sr.stoppedSince : Date.now()}`, entity: c.d.name, severity: 'bad', icon: '🚨', title: `Stopped on the highway (${c.mins} min)`, info: `${c.d.name} has been stopped ${c.mins} min on ${rev.road || 'a highway'} — possible breakdown or accident.`, text: deviceText.stoppedHighway(c.d.name, c.mins, rev.road, rev.display) });
          }
        }

        // arrivals / departures at the owner's saved places (⭐)
        try {
          const [evs, fences] = await Promise.all([
            getRecentEvents(devices.map((d) => d.id)).catch(() => []),
            getGeofences().catch(() => []),
          ]);
          checkPlaceArrivals(devices, evs, fences).forEach((al) => found.push(al));
        } catch { /* skip arrivals this round */ }

        // customer-defined alert rules
        evaluateCustomRules(devices, positions).forEach((al) => found.push(al));

        drivers.forEach((d) => {
          getDriverAlerts(d).forEach((al) => {
            found.push({ key: `drv:${d.id}:${al.icon}`, entity: d.name, ...al });
          });
        });

        // sudden braking / jumpy acceleration events from the trackers
        const events = await getRecentAlarmEvents(devices.map((d) => d.id)).catch(() => []);
        events.forEach((ev) => {
          const kind = DRIVING_ALARMS[(ev.attributes || {}).alarm];
          if (!kind) return;
          const dev = devices.find((d) => d.id === ev.deviceId);
          const alarm = (ev.attributes || {}).alarm;
          const time = new Date(ev.eventTime).toLocaleTimeString();
          const infoByAlarm = {
            accident: `Impact registered at ${time}. Call the driver and check the vehicle's location on Live Tracking now.`,
            towing: `Started at ${time}. Possible theft or unauthorized towing — check Live Tracking and follow the vehicle's movement now.`,
            jamming: `Detected at ${time}. The GPS/GSM signal is being blocked — a common theft tactic to hide a vehicle. Check on it right away.`,
            powerCut: `At ${time}. The tracker lost power — it may have been unplugged, or a fuse/connector failed. Verify the vehicle.`,
            tampering: `At ${time}. Someone is interfering with the tracker. Check the vehicle.`,
          };
          const pos = positions.find((x) => x.deviceId === ev.deviceId);
          const mapUrl = pos ? `https://www.google.com/maps?q=${pos.latitude},${pos.longitude}` : '';
          const loc = pos ? (pos.address || `${pos.latitude.toFixed(5)}, ${pos.longitude.toFixed(5)}`) : '';
          let text;
          if (alarm === 'towing') text = deviceText.towTheft(dev ? dev.name : 'Your car', loc, mapUrl);
          else if (alarm === 'accident') text = deviceText.crash(dev ? dev.name : 'Your car', loc, mapUrl);
          found.push({
            key: `evt:${ev.id}`,
            entity: dev ? dev.name : `Device ${ev.deviceId}`,
            ...kind,
            info: infoByAlarm[alarm] || `At ${time}. Repeated harsh driving may mean an unsafe driver or emergency situation.`,
            ...(text ? { text } : {}),
            // exact spot the warning was set off, for the alert-detail screen
            place: pos ? { lat: pos.latitude, lng: pos.longitude, speed: Math.round((pos.speed || 0) * 1.15078), at: ev.eventTime, device: dev ? dev.name : `Device ${ev.deviceId}`, imei: dev ? dev.uniqueId : '', address: pos.address || '', title: kind.title } : undefined,
          });
        });

        // suppress exact repeats (seen) AND same-condition repeats within cooldown
        const nowMs = Date.now();
        const cds = cooldownRef.current;
        // Discrete state-change events (ignition on/off, started moving, watch
        // hits) must fire EVERY time they happen — they are distinct events, not a
        // sustained condition, so they bypass the repeat-cooldown. The per-record
        // key + seenRef still prevents re-alerting the exact same record.
        const EVENT_PREFIXES = ['ignon:', 'ignoff:', 'moving:', 'watch:'];
        const isDiscreteEvent = (key) => EVENT_PREFIXES.some((p) => String(key).startsWith(p));
        const fresh = found.filter((al) => {
          if (seenRef.current.has(al.key)) return false;
          if (isDiscreteEvent(al.key)) return true; // always alert on a real transition
          const base = baseId(al.key);
          const window = COOLDOWN_MS[al.severity] || 30 * 60 * 1000;
          if (nowMs - (cds[base] || 0) < window) return false;
          return true;
        });
        if (fresh.length === 0) { firstRunRef.current = false; return; }

        fresh.forEach((al) => { seenRef.current.add(al.key); cds[baseId(al.key)] = nowMs; });
        saveSeen(seenRef.current);
        saveCooldowns(cds);
        // ---- freeze a snapshot of the car at the exact moment the alert fired ----
        // Only a handful of alert types used to attach a position, so most alerts
        // opened to an empty detail screen. Now EVERY alert carries the spot it
        // happened (pin-dropped on the map) plus the readings the car had right
        // then — speed, fuel, coolant, battery, RPM, odometer, ignition. Once
        // logged this is frozen history: it never re-reads live data later.
        fresh.forEach((al) => {
          const dev = devices.find((d) => d.name === al.entity)
            || devices.find((d) => String(al.key || '').split(':')[1] === String(d.id));
          const p = dev && positions.find((x) => x.deviceId === dev.id);
          if (!p) return;
          const at = (p.attributes || {});
          const mph = Math.round((p.speed || 0) * 1.15078);
          if (!al.place || al.place.lat == null) {
            al.place = {
              lat: p.latitude, lng: p.longitude, speed: mph, at: p.fixTime || new Date().toISOString(),
              device: dev.name, imei: dev.uniqueId || '', address: p.address || '', title: al.title,
            };
          }
          const num = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
          al.vitals = {
            speed: mph,
            ignition: at.ignition == null ? null : !!at.ignition,
            fuel: num(at.io48 != null ? at.io48 : at.fuel),
            coolant: num(at.io32),
            rpm: num(at.io36),
            battery: num(at.io113 != null ? at.io113 : (at.io67 != null ? at.io67 : at.power)),
            faults: num(at.io30),
            codes: at.dtcs || at.io281 || '',
            odometer: num(at.odometer != null ? at.odometer : at.totalDistance),
            sats: num(at.sat),
            fixTime: p.fixTime || null,
          };
        });
        logAlerts(fresh); // record for the daily report (keeps the log complete)

        // crashes, tows and over-speed jump to the front and are always spoken
        const urgent = (al) => al.icon === '💥' || al.icon === '🪝' || al.icon === '📡';
        const priority = (al) => urgent(al) || al.icon === '🚨' || al.icon === '⏩' || al.icon === '🔧' || ['🔌', '🚗', '✅', '➡️', '⬅️', '⛽', '🛠️', '🔔', '⏱️', '🔋'].includes(al.icon);

        // On the FIRST poll after opening, don't replay the last 15-minute window of
        // point-in-time events (arrivals/departures, ignition on/off, moving, harsh
        // driving, arrived-safe, curfew, watch hits) — that caused a burst of stacked
        // banners. They're marked seen above so they won't re-fire; sustained
        // conditions (fuel, battery, check-engine, offline) and urgent crash/tow still show.
        let display = fresh;
        if (firstRunRef.current) {
          // On the FIRST poll after opening the app, don't replay conditions that
          // already existed before you opened it — that's what made the same
          // check-engine / fuel / etc. banners pop every single time. Only truly
          // safety-critical things (crash, tow, jamming, over-speed, highway-stop)
          // still banner on open; everything else remains in the Alerts tab and only
          // banners if it NEWLY happens while you're using the app.
          const critical = (al) => urgent(al) || /^(tow:|ovspd:|jam:|hwystop:)/.test(String(al.key));
          display = fresh.filter(critical);
        }
        firstRunRef.current = false;
        // the dispatch board (/truckmate) isn't for TagAlong car alerts: no pop-ups, chime, voice or phone notifications there.
        // They're still marked seen and stay in the Alerts tab.
        if (onDispatchBoard()) { setBanners((prev) => (prev.length ? [] : prev)); return; }
        if (display.length === 0) return;

        const sorted = [...display].sort((a, b) => priority(b) - priority(a));
        setBanners((prev) => [...sorted.filter(urgent).map((al) => ({ ...al, id: al.key + Date.now() })), ...prev,
          ...sorted.filter((al) => !urgent(al)).map((al) => ({ ...al, id: al.key + Date.now() }))]);
        // when muted, banners still pop up — just no chime and no spoken voice.
        // Otherwise: one attention chime for the batch, then SPEAK only the alert
        // types the owner set to "voice" (defaults: crash, tow, jamming, speeding,
        // over-speed, check-engine). Everything else is chime-only.
        if (!mutedRef.current) {
          chime();
          sorted.filter((al) => soundForAlert(al) === 'voice').forEach((al) => {
            speak(`${urgent(al) ? 'Emergency' : al.severity === 'bad' ? 'Alert' : al.severity === 'good' ? '' : 'Warning'}. ${al.entity}. ${stripEmoji(al.title)}.`);
          });
        }
        // OS notifications (pop even when the tab/app is in the background). The
        // most urgent first, capped so a burst doesn't spam the notification tray.
        sorted.slice(0, 3).forEach(notify);
      } catch { /* server unreachable — try again next round */ }
    };

    askNotifyPermission();
    check();
    const t = setInterval(check, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <>
    {newVersion && onDispatchBoard() && (
      <div role="status" style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 5000, display: 'flex', alignItems: 'center', gap: 10, background: '#14213d', color: '#fff', borderRadius: 12, padding: '10px 14px', fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,.2)' }}>
        A new version of the console is out.
        <button type="button" onClick={() => window.location.reload()} style={{ background: '#fff', color: '#14213d', border: 0, borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer' }}>Refresh</button>
      </div>
    )}
    {detail && <AlertDetailModal alert={detail} onClose={() => setDetail(null)} />}
    {/* mute toggle — always available at the top; muted = banners pop silently */}
    <button
      className="ta-headerbtn ta-bell"
      onClick={toggleMute}
      title={muted ? 'Notifications muted — banners still pop up, but no sound. Tap to unmute.' : 'Mute notification sounds (banners still pop up).'}
      style={{
        position: 'fixed', top: '14px', right: '16px', zIndex: 5001,
        display: 'flex', alignItems: 'center', gap: '6px',
        background: muted ? 'rgba(148,163,184,0.95)' : 'rgba(15,23,42,0.85)', color: 'white',
        border: 'none', borderRadius: '999px', padding: '7px 12px', fontSize: '12px', fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,0.28)', backdropFilter: 'blur(4px)',
      }}
    >
      {muted ? '🔕 Muted' : '🔔'}
    </button>
    {/* sound settings gear — choose which alert types are spoken vs just a chime */}
    <button
      className="ta-headerbtn ta-sounds"
      onClick={() => setSoundOpen((o) => !o)}
      title="Alert sounds — pick which alerts are spoken out loud vs. just a chime"
      style={{
        position: 'fixed', top: '14px', right: '92px', zIndex: 5001,
        background: soundOpen ? 'rgba(37,99,235,0.95)' : 'rgba(15,23,42,0.85)', color: 'white',
        border: 'none', borderRadius: '999px', padding: '7px 11px', fontSize: '13px', fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,0.28)', backdropFilter: 'blur(4px)',
      }}
    >
      🔊 Sounds
    </button>
    {soundOpen && (
      <div style={{
        position: 'fixed', top: '52px', right: '16px', zIndex: 5002, width: 'min(340px, 92vw)',
        maxHeight: '70vh', overflowY: 'auto', background: 'white', borderRadius: '14px',
        boxShadow: '0 18px 44px rgba(0,0,0,0.32)', border: '1px solid #e2e8f0', padding: '14px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ fontWeight: 800, fontSize: '15px', color: '#0f172a' }}>Alert sounds</div>
          <button onClick={() => setSoundOpen(false)} style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px', lineHeight: 1.4 }}>
          Every alert pops up and chimes. Pick which ones are also <b>spoken out loud</b>.
        </div>
        {ALERT_SOUND_CATEGORIES.map((c) => {
          const val = soundPrefs[c.key] || 'chime';
          return (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '13px', color: '#0f172a', flex: 1, paddingRight: '8px' }}>{c.label}</span>
              <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '999px', padding: '2px' }}>
                {['chime', 'voice'].map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCatSound(c.key, opt)}
                    style={{
                      border: 'none', cursor: 'pointer', borderRadius: '999px', padding: '4px 10px',
                      fontSize: '11px', fontWeight: 800,
                      background: val === opt ? (opt === 'voice' ? '#2563eb' : '#0f172a') : 'transparent',
                      color: val === opt ? 'white' : '#64748b',
                    }}
                  >
                    {opt === 'voice' ? '🔊 Voice' : '🔔 Chime'}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <button
          onClick={() => speak('This is how spoken alerts will sound.')}
          style={{ marginTop: '12px', width: '100%', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a', borderRadius: '10px', padding: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
        >
          ▶︎ Test the voice
        </button>
      </div>
    )}
    {banners.length > 0 && !onDispatchBoard() && (
    <div style={{
      position: 'fixed', top: '58px', left: '50%', transform: 'translateX(-50%)',
      zIndex: 5000, display: 'flex', flexDirection: 'column', gap: '8px', width: 'min(560px, 92vw)',
    }}>
      {banners.length > 1 && (
        <button
          onClick={() => { stopSpeech(); setBanners([]); }}
          title="Dismiss every alert at once"
          style={{ alignSelf: 'flex-end', background: 'rgba(15,23,42,0.8)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '999px', padding: '6px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.3)', WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)' }}
        >
          ✕ Clear all ({banners.length})
        </button>
      )}
      {banners.slice(0, 4).map((al) => (
        <div key={al.id} style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 16px',
          borderRadius: '12px', color: 'white',
          background: al.severity === 'bad'
            ? 'linear-gradient(135deg, #dc2626, #7f1d1d)'
            : al.severity === 'good'
              ? 'linear-gradient(135deg, #16a34a, #15803d)'
              : al.severity === 'info'
                ? 'linear-gradient(135deg, #0891b2, #0e7490)'
                : 'linear-gradient(135deg, #f59e0b, #d97706)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          animation: 'alertSlideIn 0.35s ease',
        }}>
          <span style={{ fontSize: '22px' }}>{al.icon}</span>
          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => (al.place ? setDetail(al) : openOnMap(al))} title="See where it happened">
            <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '14px' }}>
              {al.entity} — {al.title}
            </div>
            {al.info && <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>{al.info}</div>}
            <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '3px', fontWeight: 700 }}>📍 Tap to see where it happened →</div>
          </div>
          <button
            onClick={() => setBanners((b) => b.filter((x) => x.id !== al.id))}
            style={{ background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '50%', width: '26px', height: '26px', padding: 0, fontSize: '14px', flexShrink: 0 }}
          >
            ×
          </button>
        </div>
      ))}
      <style>{`@keyframes alertSlideIn { from { transform: translateY(-24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
    )}
    </>
  );
}
